import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ScanLine, Camera, Trash2, CreditCard, Sparkles } from "lucide-react";
import { API, authHeaders, money, inputCls, labelCls } from "./shared";

export default function ReceiptScanner({ token, options, vehicles, onFiled }) {
  const [inbox, setInbox] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [cards, setCards] = useState([]);
  const fileRef = useRef(null);

  const load = () => {
    axios.get(`${API}/expenses/receipt-inbox?status=pending`, authHeaders(token)).then((r) => setInbox(r.data || [])).catch(() => {});
    axios.get(`${API}/expenses/cards`, authHeaders(token)).then((r) => setCards(r.data?.cards || [])).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/expenses/scan-receipt`, fd, authHeaders(token));
      toast.success(data.vendor
        ? `Receipt read: ${data.vendor} — $${Number(data.amount || 0).toFixed(2)}`
        : "Receipt saved — fill in the details below.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not scan the receipt."); }
    finally { setScanning(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="receipt-scanner" className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2"><ScanLine size={16} className="text-[#F2B705]" /> Receipt Scanner</h3>
          <p className="text-white/45 text-sm">Snap a receipt on your phone — AI reads the vendor, date and total. Assign it to a card whenever you're ready.</p>
        </div>
        <button data-testid="scan-receipt-btn" onClick={() => fileRef.current?.click()} disabled={scanning}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold hover:bg-white transition-colors disabled:opacity-60">
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {scanning ? "Reading receipt…" : "Scan Receipt"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={onFile} data-testid="scan-receipt-input" />
      </div>

      {inbox.length > 0 && (
        <div className="space-y-3">
          <p className="text-white/40 text-xs uppercase tracking-widest">Receipt Inbox — {inbox.length} waiting to be assigned</p>
          {inbox.map((r) => <InboxCard key={r.id} r={r} token={token} options={options} vehicles={vehicles} cards={cards} onDone={() => { load(); onFiled?.(); }} />)}
        </div>
      )}
    </div>
  );
}

function InboxCard({ r, token, options, vehicles, cards, onDone }) {
  const [f, setF] = useState({
    category: r.category || "Miscellaneous", amount: r.amount || "", date: r.date || "",
    vendor: r.vendor || "", payment_method: r.payment_hint || "", vehicle_id: "", notes: r.summary || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const assign = async () => {
    if (!Number(f.amount)) return toast.error("Enter the receipt amount.");
    setBusy(true);
    try {
      await axios.post(`${API}/expenses/receipt-inbox/${r.id}/assign`, { ...f, amount: Number(f.amount) }, authHeaders(token));
      toast.success(`Filed: ${f.vendor || "receipt"} — ${money(f.amount)}${f.payment_method ? ` on ${f.payment_method}` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not file the receipt."); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    if (!window.confirm("Discard this scanned receipt?")) return;
    try { await axios.delete(`${API}/expenses/receipt-inbox/${r.id}`, authHeaders(token)); onDone(); } catch { toast.error("Could not discard."); }
  };

  return (
    <div data-testid={`receipt-inbox-${r.id}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs mb-3">
        {(r.vendor || r.amount > 0) && <span className="inline-flex items-center gap-1 text-[#F2B705]"><Sparkles size={11} /> AI read</span>}
        {r.ai_note && <span className="text-white/40">{r.ai_note}</span>}
        <a href={`${API}/files/${r.receipt_path}?auth=${token}`} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white underline ml-auto">View receipt</a>
        <button data-testid={`receipt-discard-${r.id}`} onClick={discard} className="text-white/40 hover:text-red-400"><Trash2 size={13} /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <label className={labelCls}>Vendor<input data-testid={`receipt-vendor-${r.id}`} className={inputCls} value={f.vendor} onChange={(e) => set("vendor", e.target.value)} /></label>
        <label className={labelCls}>Amount<input data-testid={`receipt-amount-${r.id}`} type="number" step="0.01" className={inputCls} value={f.amount} onChange={(e) => set("amount", e.target.value)} /></label>
        <label className={labelCls}>Date<input type="date" className={`${inputCls} [color-scheme:dark]`} value={f.date} onChange={(e) => set("date", e.target.value)} /></label>
        <label className={labelCls}>Category
          <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)}>
            {(options?.expense_categories || ["Miscellaneous"]).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className={labelCls}>Card / Paid With
          <input data-testid={`receipt-card-${r.id}`} className={inputCls} list={`cards-${r.id}`} placeholder="e.g. Chase Visa •4421" value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} />
          <datalist id={`cards-${r.id}`}>{cards.map((c) => <option key={c} value={c} />)}</datalist>
        </label>
        <label className={labelCls}>Vehicle (optional)
          <select className={inputCls} value={f.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)}>
            <option value="">— none —</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}</option>)}
          </select>
        </label>
      </div>
      <div className="flex items-center justify-between mt-3">
        <input className={`${inputCls} max-w-md`} placeholder="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        <button data-testid={`receipt-assign-${r.id}`} onClick={assign} disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 text-black text-sm font-bold hover:bg-emerald-400 transition-colors disabled:opacity-60 ml-3 shrink-0">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} File as Expense
        </button>
      </div>
    </div>
  );
}
